/**
 * Step-through walkthroughs of the journeys that involve the most moving parts.
 *
 * Each beat lists what the studio did and what the system did in response,
 * because almost every question a client asks about this product is really
 * "and then what happens automatically?".
 */
import { useState } from 'react';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Playground } from './primitives';

interface Beat {
  moment: string;
  human: string;
  system: string[];
  aside?: string;
}

interface Journey {
  key: string;
  label: string;
  blurb: string;
  beats: Beat[];
}

const JOURNEYS: Journey[] = [
  {
    key: 'deposit',
    label: 'Taking a deposit',
    blurb: 'From the agent agreeing a date to the booking being locked in.',
    beats: [
      {
        moment: 'The agent agrees a date on the phone',
        human: 'Georgina pins the slot the client asked for and sends a deposit link.',
        system: [
          'A ghost booking appears on the calendar straight away, so nobody else can sell that slot.',
          'The link is given a deadline: 24 hours if the session is inside a week, 48 hours if it is further out.',
          'The client moves into the money lane of the queue and can no longer be given up on.',
        ],
        aside: 'The tighter deadline on near dates is deliberate. A slot three days out cannot sit reserved for two days.',
      },
      {
        moment: 'The client pays',
        human: 'They tap the link and pay on their phone.',
        system: [
          'The ghost booking turns into a confirmed booking with the same slot.',
          'The medical waiver goes out if the session is within four days, otherwise it is scheduled.',
          'A calendar invite is sent on whichever channel they were last spoken to.',
          'They drop out of the queue entirely. The job is done.',
        ],
      },
      {
        moment: 'Nobody pays',
        human: 'The deadline passes in silence.',
        system: [
          'The hold is released and the slot goes back on the calendar for someone else.',
          'The deposit record is kept, marked expired.',
          'A resend job appears in the queue and stays there for seven days.',
        ],
        aside: 'The record is never deleted. Every link that was ever sent stays visible in the deposit ledger.',
      },
    ],
  },
  {
    key: 'resend',
    label: 'When a link expires',
    blurb: 'The branch most people do not expect, and the reason nothing is sent automatically.',
    beats: [
      {
        moment: 'You press resend',
        human: 'The client got back to you two days late and still wants the slot.',
        system: ['Before anything is sent, the system checks whether that slot is still free.'],
      },
      {
        moment: 'If the slot is still free',
        human: 'Nothing to discuss.',
        system: [
          'A fresh link goes out for the same date.',
          'The old record is marked as superseded so you never see the dead link and its replacement side by side.',
        ],
      },
      {
        moment: 'If somebody else has taken it',
        human: 'This is the important one.',
        system: [
          'No link is sent. Not automatically, not at all.',
          'The client goes back to the top of the money lane with a note saying the date is gone and a rebook is needed.',
          'It is given a priority of 990, higher than anything else on the board except a call that is ringing right now.',
        ],
        aside:
          'Sending a payment link for a slot that no longer exists is the fastest way to lose a client. The conversation has to happen first.',
      },
    ],
  },
  {
    key: 'noshow',
    label: 'A client does not turn up',
    blurb: 'What happens to their money, and how it can be given back.',
    beats: [
      {
        moment: 'The chair sits empty',
        human: 'The manager marks the booking as a no-show and types a reason.',
        system: [
          'The deposit is forfeited for exactly its current balance, so the record lands on zero rather than going negative.',
          'The reason you typed is written onto the ledger next to the movement.',
        ],
        aside: 'The reason is mandatory. The button stays disabled until something is typed.',
      },
      {
        moment: 'They call the next day',
        human: 'Their car broke down and they want to rebook.',
        system: [
          'Only Georgina or the owner can reverse it, and the reversal is a new line on the ledger rather than an edit.',
          'The deposit goes back to paid and is unlinked from the dead booking.',
          'It reappears in the queue as money on file with no date, ready to be placed.',
        ],
      },
    ],
  },
  {
    key: 'rebook',
    label: 'Finishing a session',
    blurb: 'The checkout, and the three things it sets running afterwards.',
    beats: [
      {
        moment: 'The client is in the chair, finishing up',
        human: 'The artist asks whether they want to book the next sitting.',
        system: [
          'The artist can flag their intention, but it is only a suggestion. The manager confirms it at the counter.',
        ],
      },
      {
        moment: 'At the counter',
        human: 'They say they will rebook but do not want to pick a date today.',
        system: [
          'Their deposit rolls forward into a fresh pot, good for thirty days, waiting for a date.',
          'A follow-up job is created carrying whatever note you typed.',
          'The artist is still paid on the full session price. Rolling the deposit changes when the money arrives, not what anyone earns.',
        ],
      },
      {
        moment: 'Seven days later',
        human: 'Nobody has done anything.',
        system: [
          'A healing check appears in the queue: a natural reason to call and a natural opening to talk about the next sitting.',
          'The follow-up job is still there, slowly losing priority so it does not crowd out fresher work.',
          'After five days it drops another notch, but it never disappears on its own.',
        ],
        aside:
          'For three weeks either side of a finished session, this client is also held back from gap-filling offers. Skin needs to heal before it is worked on again.',
      },
    ],
  },
  {
    key: 'confirm',
    label: 'The week before a session',
    blurb: 'Two touches, not five. What each one is for.',
    beats: [
      {
        moment: 'Seven days out',
        human: 'A call appears on the confirmations list.',
        system: [
          'If the waiver is still unsigned, the script folds in a nudge to sign it.',
          'This is the last point at which the slot can realistically be refilled if they cancel, which is why it is a call and not a text.',
        ],
      },
      {
        moment: 'Two days out',
        human: 'A text is drafted for you to approve.',
        system: ['A short confirmation. Two days is enough notice to react, close enough that plans are firm.'],
        aside:
          'There is no day-of message. Once the day arrives it is the floor team’s job, not the confirmations list’s.',
      },
      {
        moment: 'If they never respond',
        human: 'The booking rolls into the chase list.',
        system: [
          'Nothing is carried forward or stored. It simply reappears every day until somebody resolves it.',
          'Every confirmation ends in exactly one of four outcomes: confirmed, rescheduled, cancelled, or no answer. Nothing falls through quietly.',
        ],
      },
      {
        moment: 'If they cancel and had paid a deposit',
        human: 'You take the cancellation.',
        system: [
          'The original booking is kept for the calendar history rather than deleted.',
          'The deposit is unhooked and lands back in the queue as money looking for a date.',
        ],
      },
    ],
  },
];

export default function JourneyStepper() {
  const [journeyKey, setJourneyKey] = useState(JOURNEYS[0]!.key);
  const [step, setStep] = useState(0);

  const journey = JOURNEYS.find((j) => j.key === journeyKey) ?? JOURNEYS[0]!;
  const beat = journey.beats[Math.min(step, journey.beats.length - 1)]!;
  const atStart = step === 0;
  const atEnd = step >= journey.beats.length - 1;

  const pick = (key: string) => {
    setJourneyKey(key);
    setStep(0);
  };

  return (
    <Playground
      title="Walk it through"
      instruction="Pick a situation and step through it one beat at a time. The left column is what a person does, the right is what happens without anyone asking."
    >
      <div className="flex flex-wrap gap-1.5">
        {JOURNEYS.map((j) => (
          <button
            key={j.key}
            type="button"
            onClick={() => pick(j.key)}
            aria-pressed={j.key === journey.key}
            className={clsx(
              'rounded-full border px-3.5 py-1.5 font-inter text-xs uppercase tracking-wider transition',
              j.key === journey.key
                ? 'border-gold-bright bg-gold/15 text-gold-bright'
                : 'border-gold-dim/30 text-cream-soft hover:border-gold-dim/60 hover:text-cream',
            )}
          >
            {j.label}
          </button>
        ))}
      </div>

      <p className="font-serif text-cream-faint text-sm mt-3">{journey.blurb}</p>

      {/* Step rail */}
      <div className="mt-5 flex items-center gap-1.5">
        {journey.beats.map((b, i) => (
          <button
            key={b.moment}
            type="button"
            onClick={() => setStep(i)}
            aria-label={`Step ${i + 1}: ${b.moment}`}
            aria-current={i === step}
            className={clsx(
              'h-1.5 flex-1 rounded-full transition',
              i === step ? 'bg-gold-bright' : i < step ? 'bg-gold-dim/60' : 'bg-gold-dim/20',
            )}
          />
        ))}
      </div>

      <div className="mt-5 rounded-lg border border-gold-dim/25 bg-ink-black p-5">
        <p className="label-small-caps text-gold-bright">
          Step {step + 1} of {journey.beats.length}
        </p>
        <h4 className="heading-display text-xl text-cream mt-1.5">{beat.moment}</h4>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <p className="label-small-caps text-cream-faint">Someone does this</p>
            <p className="font-serif text-cream-soft mt-1.5 leading-relaxed">{beat.human}</p>
          </div>
          <div>
            <p className="label-small-caps text-mint">The system does this</p>
            <ul className="mt-1.5 space-y-2">
              {beat.system.map((s) => (
                <li key={s} className="flex gap-2 font-serif text-cream-soft leading-relaxed">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-mint" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {beat.aside ? (
          <div className="mt-4 rounded-md border-l-2 border-azure/60 bg-azure/10 px-4 py-3">
            <p className="font-serif text-cream-soft text-sm leading-relaxed">{beat.aside}</p>
          </div>
        ) : null}

        <div className="mt-5 flex items-center justify-between gap-3 border-t border-gold-dim/15 pt-4">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={atStart}
            className={clsx('btn-quiet inline-flex items-center gap-1.5', atStart && 'opacity-30')}
          >
            <ChevronLeft size={13} /> Back
          </button>
          {atEnd ? (
            <button type="button" onClick={() => setStep(0)} className="btn-ghost">
              Start over
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStep((s) => Math.min(journey.beats.length - 1, s + 1))}
              className="btn-primary inline-flex items-center gap-1.5"
            >
              What happens next <ChevronRight size={13} />
            </button>
          )}
        </div>
      </div>
    </Playground>
  );
}
